import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ConnectionTestPage() {
  const [results, setResults] = useState([]);
  const [testing, setTesting] = useState(false);

  const addResult = (message, success = true) => {
    setResults(prev => [...prev, { message, success, time: new Date().toLocaleTimeString() }]);
  };

  const runTests = async () => {
    setResults([]);
    setTesting(true);

    // Test 1: Check if backend URL is defined
    addResult(`Backend URL: ${BACKEND_URL}`);
    addResult(`API Endpoint: ${API}`);
    addResult(`Current Origin: ${window.location.origin}`);

    // Test 2: Try simple GET request
    try {
      addResult("Testing GET /api/auth/me...");
      await axios.get(`${API}/auth/me`, { 
        withCredentials: true,
        timeout: 10000 
      });
      addResult("✅ GET request succeeded (or returned 401 - server is reachable)");
    } catch (error) {
      if (error.response) {
        addResult(`✅ Server responded with status: ${error.response.status}`);
      } else if (error.request) {
        addResult(`❌ No response from server: ${error.message}`, false);
      } else {
        addResult(`❌ Request setup failed: ${error.message}`, false);
      }
    }

    // Test 3: Try POST with body
    try {
      addResult("Testing POST /api/auth/session...");
      const response = await axios.post(`${API}/auth/session`, {
        session_id: "test-session-id-123"
      }, {
        withCredentials: true,
        timeout: 10000
      });
      addResult("✅ POST request succeeded");
    } catch (error) {
      if (error.response) {
        addResult(`✅ Server responded with status: ${error.response.status} - ${error.response.data?.detail || 'Error'}`);
      } else if (error.request) {
        addResult(`❌ No response from server: ${error.message}`, false);
      } else {
        addResult(`❌ Request setup failed: ${error.message}`, false);
      }
    }

    // Test 4: Check CORS preflight
    try {
      addResult("Testing CORS preflight...");
      const response = await fetch(`${API}/auth/session`, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'content-type'
        }
      });
      addResult(`✅ CORS preflight returned: ${response.status}`);
    } catch (error) {
      addResult(`❌ CORS preflight failed: ${error.message}`, false);
    }

    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold mb-6 text-gray-900">Connection Test</h1>
        
        <button
          onClick={runTests}
          disabled={testing}
          className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 mb-6"
        >
          {testing ? "Testing..." : "Run Connection Tests"}
        </button>

        <div className="space-y-2">
          {results.map((result, index) => (
            <div
              key={index}
              className={`p-3 rounded ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
            >
              <span className="text-xs text-gray-500 mr-2">{result.time}</span>
              <span className="font-mono text-sm">{result.message}</span>
            </div>
          ))}
        </div>

        {results.length === 0 && !testing && (
          <div className="text-gray-500 text-center py-8">
            Click "Run Connection Tests" to diagnose connectivity issues
          </div>
        )}
      </div>
    </div>
  );
}
